import React from "react";
import { motion } from "framer-motion";
import { BookOpen, MapPin, ArrowRight } from "lucide-react";

const TOPICS = [
  {
    t: "¿Qué significa una atención médica centrada en la paciente?",
    chip: "Modelo de atención",
    excerpt: "El enfoque que coloca tu bienestar y experiencia en el centro de cada decisión clínica.",
  },
  {
    t: "La importancia del seguimiento durante el embarazo",
    chip: "Control prenatal",
    excerpt: "Por qué cada cita del embarazo construye seguridad para mamá y bebé.",
  },
  {
    t: "Seguridad del paciente en ginecología y obstetricia",
    chip: "Calidad clínica",
    excerpt: "Protocolos hospitalarios aplicados a la consulta privada.",
  },
  {
    t: "Cómo elegir un ginecólogo para tu embarazo",
    chip: "Guía práctica",
    excerpt: "Criterios profesionales y humanos para una decisión informada.",
  },
  {
    t: "Cómo una atención médica organizada mejora los resultados del embarazo",
    chip: "Calidad y gestión",
    excerpt: "El impacto medible de los procesos claros en la salud materno-fetal.",
  },
];

const ZONES = [
  "Tepotzotlán",
  "Cuautitlán Izcalli",
  "Cuautitlán",
  "Teoloyucan",
  "Huehuetoca",
  "Zumpango",
];

export default function BlogPreview() {
  return (
    <section
      data-testid="blog-preview"
      className="relative py-20 md:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white to-brand-snow" />

      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-12 items-end">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.28em] text-brand-medical mb-3">
              <BookOpen className="inline w-3.5 h-3.5 mr-1.5" strokeWidth={1.6} />
              Educación para la salud femenina
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-deep leading-[1.05]">
              Información médica clara, <em className="not-italic text-gradient-blue">para mujeres informadas</em>.
            </h2>
          </div>
          <div className="lg:col-span-5 text-brand-deep/70 font-light text-lg leading-relaxed">
            Próximamente: artículos médicos revisados por el Dr. Jairo Franco con
            información clínica accesible y útil para tu salud.
          </div>
        </div>

        {/* Topics */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {TOPICS.map((p, i) => (
            <motion.article
              key={p.t}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.07 }}
              whileHover={{ y: -4 }}
              data-testid={`blog-topic-${i}`}
              className="group relative rounded-3xl bg-white hairline premium-shadow hover:premium-shadow-hover p-7 overflow-hidden transition-shadow duration-500"
            >
              <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 rounded-full bg-brand-cloud px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-brand-medical">
                {p.chip}
              </div>
              <p className="text-xs uppercase tracking-[0.25em] text-brand-deep/50 mt-1 mb-3">Próximamente</p>
              <h3 className="font-serif text-xl text-brand-deep leading-snug mb-3 pr-20">{p.t}</h3>
              <p className="text-sm text-brand-deep/60 font-light leading-relaxed">{p.excerpt}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-brand-medical opacity-50 group-hover:opacity-100 transition-opacity">
                Leer artículo <ArrowRight className="w-3 h-3" />
              </div>
            </motion.article>
          ))}
        </div>

        {/* Local SEO zones */}
        <div className="mt-12 rounded-3xl bg-gradient-to-br from-brand-cloud to-brand-silver/60 hairline p-7 md:p-9">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-2xl bg-white ring-1 ring-brand-silver/70 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-brand-medical" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-brand-medical mb-1.5">Atención en tu zona</p>
              <p className="font-serif text-xl md:text-2xl text-brand-deep leading-snug mb-3">
                Ginecólogo y obstetra de confianza para pacientes en el norte del Estado de México.
              </p>
              <ul className="flex flex-wrap gap-2">
                {ZONES.map((z) => (
                  <li
                    key={z}
                    data-testid={`zone-${z.toLowerCase().replace(/\s+/g, "-")}`}
                    className="rounded-full bg-white px-3.5 py-1.5 text-xs font-medium text-brand-deep hairline"
                  >
                    {z}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
